// Pure ISBN-10 / ISBN-13 validation + conversion. Browser- and SSR-safe.
import { computeCheckDigit } from "./gtin";

export type IsbnKind = "ISBN-10" | "ISBN-13";

export type IsbnValid = {
  ok: true;
  kind: IsbnKind;
  normalized: string;
  checkDigit: string;
  /** The other format, when convertible. ISBN-10 <-> ISBN-13 (978 prefix). */
  converted?: { kind: IsbnKind; value: string };
};

export type IsbnInvalid = {
  ok: false;
  cleaned: string;
  reason: string;
  kind?: IsbnKind;
  expected?: string;
};

export type IsbnResult = IsbnValid | IsbnInvalid;

/** Strip whitespace, dashes, underscores, dots. Uppercase trailing X. */
export function cleanIsbn(raw: string): string {
  return raw.replace(/[\s\-_.]/g, "").toUpperCase();
}

/** ISBN-10 mod-11 check character (digit or 'X'). */
export function isbn10CheckChar(body9: string): string {
  if (!/^\d{9}$/.test(body9)) throw new Error("ISBN-10 body must be 9 digits");
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(body9[i]) * (10 - i);
  const r = (11 - (sum % 11)) % 11;
  return r === 10 ? "X" : String(r);
}

/** ISBN-13 uses the standard GS1 mod-10 (same as EAN-13). */
export function isbn13CheckDigit(body12: string): number {
  if (!/^\d{12}$/.test(body12)) throw new Error("ISBN-13 body must be 12 digits");
  return computeCheckDigit(body12);
}

/** Convert a valid ISBN-10 to its ISBN-13 (always 978 prefix). */
export function isbn10To13(isbn10: string): string {
  const c = cleanIsbn(isbn10);
  const body = "978" + c.slice(0, 9);
  return body + String(isbn13CheckDigit(body));
}

/**
 * Convert an ISBN-13 to ISBN-10. Only ISBN-13s in the 978 range are
 * convertible; 979-prefixed codes have no ISBN-10 equivalent.
 */
export function isbn13To10(isbn13: string): string | null {
  const c = cleanIsbn(isbn13);
  if (!c.startsWith("978")) return null;
  const body = c.slice(3, 12);
  return body + isbn10CheckChar(body);
}

export function validateIsbn(raw: string): IsbnResult {
  const cleaned = cleanIsbn(raw);
  if (!cleaned) {
    return { ok: false, cleaned, reason: "Enter an ISBN to validate." };
  }

  // ISBN-10
  if (cleaned.length === 10) {
    if (!/^\d{9}[\dX]$/.test(cleaned)) {
      return {
        ok: false,
        cleaned,
        kind: "ISBN-10",
        reason: "ISBN-10 must be 9 digits plus a final digit or 'X'.",
      };
    }
    const expected = isbn10CheckChar(cleaned.slice(0, 9));
    if (expected !== cleaned[9]) {
      return {
        ok: false,
        cleaned,
        kind: "ISBN-10",
        expected,
        reason: `Invalid ISBN-10 check digit. Expected '${expected}', got '${cleaned[9]}'.`,
      };
    }
    const converted = { kind: "ISBN-13" as const, value: isbn10To13(cleaned) };
    return {
      ok: true,
      kind: "ISBN-10",
      normalized: cleaned,
      checkDigit: cleaned[9],
      converted,
    };
  }

  // ISBN-13
  if (cleaned.length === 13) {
    if (!/^\d{13}$/.test(cleaned)) {
      return {
        ok: false,
        cleaned,
        kind: "ISBN-13",
        reason: "ISBN-13 must be exactly 13 digits.",
      };
    }
    if (!cleaned.startsWith("978") && !cleaned.startsWith("979")) {
      return {
        ok: false,
        cleaned,
        kind: "ISBN-13",
        reason: "ISBN-13 must start with the Bookland prefix 978 or 979.",
      };
    }
    const expected = String(isbn13CheckDigit(cleaned.slice(0, 12)));
    if (expected !== cleaned[12]) {
      return {
        ok: false,
        cleaned,
        kind: "ISBN-13",
        expected,
        reason: `Invalid ISBN-13 check digit. Expected '${expected}', got '${cleaned[12]}'.`,
      };
    }
    const ten = isbn13To10(cleaned);
    return {
      ok: true,
      kind: "ISBN-13",
      normalized: cleaned,
      checkDigit: cleaned[12],
      converted: ten ? { kind: "ISBN-10", value: ten } : undefined,
    };
  }

  return {
    ok: false,
    cleaned,
    reason: `Expected 10 or 13 characters after cleanup, got ${cleaned.length}.`,
  };
}

export function isbnKindDescription(kind: IsbnKind): string {
  return kind === "ISBN-10"
    ? "Legacy 10-character book identifier (pre-2007). Uses a mod-11 check digit where 'X' represents 10."
    : "Modern 13-digit book identifier. Same structure as EAN-13 with a Bookland prefix of 978 or 979.";
}
